import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { useDetailCRUD } from 'hooks/detail/useDetailCRUD';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { ICardInputWithTypeAndId } from 'libs/types/card/cardTypes';
import { IUpdateDetailInput } from 'libs/types/detail/detailTypes';
import { createContext, useState } from 'react';

export interface ICtxDetail {
    selectedDetailId: string;
    updateDetailInput: IUpdateDetailInput;
    updatingIndex: number;
    saving: boolean;
    lastSaved: Date;
    lastUpdated: Date;
    isSaved: boolean;
    isNeedingToSave: boolean;
    map: { worldMapId: string; title: string };
    markers: WorldMapMarkerStory_worldMapView_markers[];
    setMarkers: (markers: WorldMapMarkerStory_worldMapView_markers[]) => void;
    setMap: (map: { worldMapId: string; title: string }) => void;
    setSelectedDetailId: (selectedDetailId: string) => void;
    addCardByPosition: (value: ICardInputWithTypeAndId, position: number) => void;
    updateCardByPosition: (value: ICardInputWithTypeAndId, position: number) => void;
    deleteCardByPosition: (position: number) => void;
    moveCardByPosition: (from: number, to: number) => void;
    setUpdatingIndex: (updatingIndex: number) => void;
    setSaving: (saving: boolean) => void;
    changeDetailInput: (
        input: IUpdateDetailInput,
        options: { triggerHistory: boolean; saveImmediately?: boolean }
    ) => void;
    save: () => void;
    reset: () => void;
}

export const CtxDetail = createContext<ICtxDetail>(null);

export const DetailStateProvider = ({ children }: { children: React.ReactNode }) => {
    const [selectedDetailId, setSelectedDetailId] = useState<string>(null);
    const [updateDetailInput, setUpdateDetailInput] = useState<IUpdateDetailInput>(null);
    const [updatingIndex, setUpdatingIndex] = useState<number>(-1);
    const [saving, setSaving] = useState<boolean>(false);
    const [lastSaved, setLastSaved] = useState<Date>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(null);
    const [map, setMap] = useState<{ worldMapId: string; title: string }>(null);
    const detailCRUD = useDetailCRUD(map?.worldMapId);
    const isSaved = !saving && (lastUpdated < lastSaved || !lastUpdated);
    const isNeedingToSave = !!(!saving && (lastUpdated > lastSaved || (!lastSaved && lastUpdated)));
    const [markers, setMarkers] = useState<WorldMapMarkerStory_worldMapView_markers[]>(null);

    function reset() {
        setSelectedDetailId(null);
        setUpdateDetailInput(null);
        setUpdatingIndex(-2);
        setSaving(false);
        setLastSaved(null);
        setLastUpdated(null);
    }

    async function saveInner(input?: IUpdateDetailInput) {
        setSaving(true);
        setLastSaved(new Date());
        await detailCRUD.updateDetail(input ?? updateDetailInput);
        setSaving(false);
    }

    const saveDebounced = useMapheimDebounced(saveInner, 2000);

    async function save() {
        setSaving(true);
        saveDebounced(updateDetailInput);
    }

    function changeDetailInput(
        input: IUpdateDetailInput,
        options: { triggerHistory: boolean; saveImmediately?: boolean }
    ) {
        setUpdateDetailInput(input);
        if (options.triggerHistory) {
            setLastUpdated(new Date());
            if (options.saveImmediately) {
                saveDebounced.cancel();
                saveInner(input);
            } else {
                saveDebounced(input);
            }
        }
    }

    function addCardByPosition(value: ICardInputWithTypeAndId, position: number) {
        const newCards = [...updateDetailInput.cards];
        newCards.splice(position, 0, value);
        changeDetailInput(
            {
                ...updateDetailInput,
                cards: newCards
            },
            { triggerHistory: true, saveImmediately: true }
        );
    }

    function updateCardByPosition(value: ICardInputWithTypeAndId, position: number) {
        const newCards = [...updateDetailInput.cards];
        newCards.splice(position, 1, {
            ...newCards[position],
            ...value
        });
        changeDetailInput(
            {
                ...updateDetailInput,
                cards: newCards
            },
            { triggerHistory: true, saveImmediately: false }
        );
    }

    function deleteCardByPosition(position: number) {
        const newCards = [...updateDetailInput.cards];
        newCards.splice(position, 1);
        changeDetailInput(
            {
                ...updateDetailInput,
                cards: newCards
            },
            { triggerHistory: true, saveImmediately: true }
        );
        setUpdatingIndex(-2);
    }

    function moveCardByPosition(from: number, to: number) {
        const newCards = [...updateDetailInput.cards];
        const card = newCards.splice(from, 1);
        newCards.splice(to, 0, card[0]);
        changeDetailInput(
            {
                ...updateDetailInput,
                cards: newCards
            },
            { triggerHistory: true, saveImmediately: true }
        );
        setUpdatingIndex(to);
    }

    return (
        <CtxDetail.Provider
            value={{
                markers,
                setMarkers,
                selectedDetailId,
                setSelectedDetailId,
                updateDetailInput,
                addCardByPosition,
                updateCardByPosition,
                deleteCardByPosition,
                moveCardByPosition,
                updatingIndex,
                setUpdatingIndex,
                save,
                saving,
                setSaving,
                lastSaved,
                lastUpdated,
                changeDetailInput,
                isSaved,
                isNeedingToSave,
                reset,
                map,
                setMap
            }}
        >
            {children}
        </CtxDetail.Provider>
    );
};
